import React, { useState, useRef, useCallback, useMemo, useEffect } from "react";
import { VariableSizeGrid as Grid } from 'react-window';
import AutoSizer from 'react-virtualized-auto-sizer'; // For dynamic width/height

import SmartTableHiddenFields from "./components/smartTable___buttons/hiddenFields/HiddenColumns";
import SmartTableFilters from "./components/smartTable___buttons/filters/SmartTable___Filters";
import SmartTableSorts from "./components/smartTable___buttons/sorts/SmartTable___Sorts";
import SmartTableGrouping from "./components/smartTable___buttons/grouping/SmartTable___Grouping";
import SmartTableConditionalFormatting from "./components/smartTable___buttons/conditionalFormatting/SmartTable___ConditionalFormatting";
import ViewsList from "./components/SmartTable___views/ViewsList";
import { ChevronLeftIcon, PencilAltIcon } from "@heroicons/react/outline";
import Layout from "../layout/Layout";
import { InformationCircleIcon, SearchIcon, XIcon } from "@heroicons/react/solid";

import { classNames } from "./../misc/styling";
import { AiOutlineLoading3Quarters } from "react-icons/ai";
import FadeIn from "../transitions/FadeIn";
import useFetchSmartTableData, { useSmartTableViewMetadata } from "./hooks/useFetchSmartTableRawData";
import { applyClientSideProcessing } from "./utils/clientSideProcessing";
import { useParams } from "react-router-dom/cjs/react-router-dom.min";
import useNav from "../../hooks/useNav";
import useKeyboardShortcut from "../../hooks/useKeyShortcut";

// Import cell components previously in SmartTable___Row.jsx
import SingleSelect from "./components/smartTable___rowComponents/SingleSelect";
import HouseholdLink from "./components/smartTable___rowComponents/HouseholdLink";
import DateComponent from "./components/smartTable___rowComponents/Date"; // Renamed to avoid conflict with JS Date
import SmartTableNumber from "./components/smartTable___rowComponents/SmartTable___Number";
import AdvisorColumn from "./components/smartTable___rowComponents/AdvisorColumn";
import Checkbox from './components/smartTable___rowComponents/Checkbox';
import ButtonComponent from './components/smartTable___rowComponents/Button'; // Renamed to avoid conflict
import CopyColumn from "./components/smartTable___rowComponents/CopyColumn";
import MultiSelect from "./components/smartTable___rowComponents/MultiSelect";
import ClaimHousehold from "./components/smartTable___rowComponents/ClaimHousehold";
import LinkColumn from "./components/smartTable___rowComponents/LinkColumn";
import { useSelector } from "react-redux"; // For accessToken
import { SummaryColumnDisplay } from "./components/Summaries"; // Import the exported function
import Text from "./components/smartTable___rowComponents/Text";
import socketIoConnection from "../../socketIoConnect"; // Added import
import { useQueryClient, useMutation } from "react-query"; // Added for cache management
import { ApiRequest, API_AUTH_TYPES, API_ROUTES } from "../../api";

const ROW_HEIGHT = 50;
const DEFAULT_COLUMN_WIDTH = 200;
const FROZEN_COLUMN_WIDTH = 400;

const viewsApi = new ApiRequest(
  API_ROUTES.SMART_TABLE_VIEWS,
  API_AUTH_TYPES.ADVISOR
);



const SmartTable = ({ tableId, customControlButtons, buttonFunctions, urlBase }) => {
  const [isShowingViews, setIsShowingViews] = useState(false);
  const { view_id: selectedViewId } = useParams();
  const nav = useNav();
  const queryClient = useQueryClient(); // For cache management
  const setSelectedViewId = (viewId) => {
    nav(`/crm/${urlBase}/smart-table/${viewId}`);
  };

  const [socket, setSocket] = useState(null);

  // State to track which cell is currently being edited
  const [editingCell, setEditingCell] = useState(null); // {rowId, colId}

  // Custom tooltip state
  const [tooltip, setTooltip] = useState({
    show: false,
    content: '',
    x: 0,
    y: 0
  });

  // Tooltip handlers
  const showTooltip = (event, title, description) => {
    const rect = event.currentTarget.getBoundingClientRect();
    const content = description ? (
      <div>
        <div className="font-semibold mb-1">{title}</div>
        <div className="text-sm text-gray-600">{description}</div>
      </div>
    ) : title;

    setTooltip({
      show: true,
      content,
      x: rect.left + rect.width / 2,
      y: rect.bottom + 10
    });
  };

  const hideTooltip = () => {
    setTooltip(prev => ({ ...prev, show: false }));
  };

  // OPTIMIZATION: Single API call gets all data + all views (cached by tableId only)
  // View switching becomes instant client-side operations!
  const {
    allRows,
    metadata,
    isLoading,
    isError,
    isFetching,
    refetch
  } = useFetchSmartTableData(tableId, selectedViewId);

  // OPTIMIZATION: Get view-specific metadata from cached data (no API calls!)
  // This runs instantly when selectedViewId changes
  const viewMetadata = useSmartTableViewMetadata(metadata, selectedViewId);

  // Create initial metadata by combining cached metadata and view-specific data
  // This is now purely client-side and instant when switching views
  const initialMetadata = useMemo(() => {
    if (!metadata?.columns || !viewMetadata) return null;

    console.log(`🎯 Client-side view switch to: ${viewMetadata.view_name} (${viewMetadata.view_id})`);

    return {
      columns: metadata.columns,
      filters: viewMetadata.filters || [],
      sorts: viewMetadata.sorts || [],
      hidden_columns: viewMetadata.hidden_columns || [],
      column_order: viewMetadata.column_order || [],
      column_widths: viewMetadata.column_widths || {},
      conditional_formatting: viewMetadata.conditional_formatting || [],
      grouping: viewMetadata.grouping || [],
      summary_preferences: viewMetadata.summary_preferences || {},
      view_id: viewMetadata.view_id,
      view_name: viewMetadata.view_name,
      table_name: metadata.table_name,
      all_views: metadata.all_views || [],
      total_rows_length: allRows?.length || 0,
    };
  }, [metadata, viewMetadata, allRows]);

  useEffect(() => {
    if (!tableId) return; // Ensure tableId is present before connecting

    const newSocket = socketIoConnection(`/smart-table/${tableId}`);
    setSocket(newSocket);

    newSocket.on('connect', () => {
      console.log(`Socket connected for table: ${tableId}`);
      // Automatically join the table room upon connection
      newSocket.emit('join_table', { tableId });
    });

    // Listener for successful update acknowledgment (covers our own updates)
    newSocket.on('cell_update_ack', (data) => {
      // data: { status: 'success', tableId, rowId, colId, newValue }
      console.log(`✅ OPTIMISTIC UPDATE CONFIRMED by server:`, data);
      // Update the cache directly to persist the change while preserving view settings
      queryClient.setQueryData(["smart-table-complete-data", { tableId }], (oldData) => {
        if (!oldData) return oldData;

        return {
          ...oldData,
          rows: oldData.rows.map(row =>
            row.row_id === data.rowId ? { ...row, [data.colId]: data.newValue } : row
          ),
          // Preserve all view settings
          view: oldData.view
        };
      });
    });

    // Listener for updates broadcast by the server (e.g., changes from other users)
    newSocket.on('cell_updated_by_server', (data) => {
      // data: { tableId, rowId, colId, newValue, updatedBy (optional) }
      console.log(`🔄 REMOTE UPDATE received from another client:`, data);
      // Update the cache directly while preserving view settings
      queryClient.setQueryData(["smart-table-complete-data", { tableId }], (oldData) => {
        if (!oldData) return oldData;

        return {
          ...oldData,
          rows: oldData.rows.map(row =>
            row.row_id === data.rowId ? { ...row, [data.colId]: data.newValue } : row
          ),
          // Preserve all view settings
          view: oldData.view
        };
      });
    });

    // Listener for errors from the server after an update attempt
    newSocket.on('cell_update_error', (errorData) => {
      // errorData: { status: 'error', message, tableId, rowId, colId, originalValue (optional) }
      console.error(`❌ OPTIMISTIC UPDATE FAILED:`, errorData);
      // Notify user (e.g., toast message)
      alert(`Error updating cell: ${errorData.message}`); // Simple alert for now

      // Rollback the optimistic update in the cache while preserving view settings
      if (errorData.originalValue !== undefined) {
        console.log(`🔙 ROLLING BACK optimistic update for ${errorData.colId} to "${errorData.originalValue}"`);
        queryClient.setQueryData(["smart-table-complete-data", { tableId }], (oldData) => {
          if (!oldData) return oldData;

          return {
            ...oldData,
            rows: oldData.rows.map(row =>
              row.row_id === errorData.rowId ? { ...row, [errorData.colId]: errorData.originalValue } : row
            ),
            // Preserve all view settings
            view: oldData.view
          };
        });
      }
    });

    newSocket.on('disconnect', () => {
      console.log(`Socket disconnected for table: ${tableId}`);
    });

    return () => {
      if (newSocket) {
        // Leave the table room before disconnecting
        newSocket.emit('leave_table', { tableId });
        newSocket.off('cell_update_ack');
        newSocket.off('cell_updated_by_server');
        newSocket.off('cell_update_error');
        newSocket.disconnect();
      }
    };
  }, [tableId, queryClient]); // Updated dependencies

  const [effectiveFilters, setEffectiveFilters] = useState([]);
  const [effectiveSorts, setEffectiveSorts] = useState([]);
  const [effectiveHiddenColumns, setEffectiveHiddenColumns] = useState([]);
  const [effectiveColumnOrder, setEffectiveColumnOrder] = useState([]);
  const [effectiveColumnWidths, setEffectiveColumnWidths] = useState({});
  const [effectiveConditionalFormatting, setEffectiveConditionalFormatting] = useState([]);
  const [effectiveGrouping, setEffectiveGrouping] = useState([]);
  const [effectiveSummaryPreferences, setEffectiveSummaryPreferences] = useState({});

  // Search state
  const [searchQuery, setSearchQuery] = useState("");
  const [isSearchActive, setIsSearchActive] = useState(false);

  const [baseMetadata, setBaseMetadata] = useState(null);

  // Keyboard shortcut for Ctrl+F to activate search
  useKeyboardShortcut(() => {
    if (isSearchActive && searchInputRef.current) {
      // If search is already active, focus the input
      searchInputRef.current.focus();
    } else {
      // If search is not active, activate it
      setIsSearchActive(true);
    }
  }, "KeyF", true, false, false);

  useEffect(() => {
    if (initialMetadata) {
      setEffectiveFilters(initialMetadata.filters || []);
      setEffectiveSorts(initialMetadata.sorts || []);
      setEffectiveHiddenColumns(initialMetadata.hidden_columns || []);
      setEffectiveColumnOrder(initialMetadata.column_order || []);
      setEffectiveColumnWidths(initialMetadata.column_widths || {});
      setEffectiveConditionalFormatting(initialMetadata.conditional_formatting || []);
      setEffectiveGrouping(initialMetadata.grouping || []);
      setEffectiveSummaryPreferences(initialMetadata.summary_preferences || {});
      setBaseMetadata({
        ...initialMetadata,
        total_rows_length: allRows?.length || 0, // Use allRows for total count from server
      });
    }
  }, [initialMetadata, allRows]);

  const processedTableData = useMemo(() => {
    // Use optimisticRows for display, if available, otherwise fall back to allRows or empty
    const sourceRows = allRows;
    if (!sourceRows || !baseMetadata?.columns) {
      return { rows: [], visible_columns: [], summaries: {}, filtered_rows_length: 0 };
    }
    return applyClientSideProcessing(
      sourceRows,
      baseMetadata.columns,
      effectiveFilters,
      effectiveSorts,
      effectiveHiddenColumns,
      effectiveColumnOrder,
      searchQuery,
      effectiveGrouping
    );
  }, [allRows, baseMetadata?.columns, effectiveFilters, effectiveSorts, effectiveHiddenColumns, effectiveColumnOrder, searchQuery, effectiveGrouping]);

  const {
    rows: displayRows,
    visible_columns: displayVisibleColumns,
    summaries: displaySummaries,
    filtered_rows_length: displayFilteredRowsLength
  } = processedTableData;
  const metadataForControls = useMemo(() => {
    if (!baseMetadata) return null;
    return {
      ...baseMetadata,
      filters: effectiveFilters,
      sorts: effectiveSorts,
      hidden_columns: effectiveHiddenColumns,
      visible_columns: displayVisibleColumns,
      summaries: displaySummaries,
      filtered_rows_length: displayFilteredRowsLength,
      total_rows_length: allRows?.length || 0, // Total should reflect server state
      grouping: effectiveGrouping,
      summary_preferences: effectiveSummaryPreferences,
    };
  }, [baseMetadata, effectiveFilters, effectiveSorts, effectiveHiddenColumns, displayVisibleColumns, displaySummaries, displayFilteredRowsLength, allRows, effectiveGrouping, effectiveSummaryPreferences]);

  const handleFiltersChange = useCallback((newFilters) => {
    setEffectiveFilters(newFilters);
  }, []);

  const handleSortsChange = useCallback((newSorts) => {
    setEffectiveSorts(newSorts);
  }, []);

  const handleHiddenColumnsChange = useCallback((newHiddenColumns) => {
    setEffectiveHiddenColumns(newHiddenColumns);
  }, []);

  const handleColumnOrderChange = useCallback((newColumnOrder) => {
    setEffectiveColumnOrder(newColumnOrder);
  }, []);

  const handleColumnWidthsChange = useCallback((newColumnWidths) => {
    setEffectiveColumnWidths(newColumnWidths);

    // Reset grid caches to update column widths
    if (frozenGridRef.current) {
      frozenGridRef.current.resetAfterColumnIndex(0);
    }
    if (frozenHeaderGridRef.current) {
      frozenHeaderGridRef.current.resetAfterColumnIndex(0);
    }
    if (frozenFooterGridRef.current) {
      frozenFooterGridRef.current.resetAfterColumnIndex(0);
    }
    if (scrollableGridRef.current) {
      scrollableGridRef.current.resetAfterColumnIndex(0);
    }
    if (scrollableHeaderGridRef.current) {
      scrollableHeaderGridRef.current.resetAfterColumnIndex(0);
    }
    if (scrollableFooterGridRef.current) {
      scrollableFooterGridRef.current.resetAfterColumnIndex(0);
    }
  }, []);

  const handleConditionalFormattingChange = useCallback((newConditionalFormatting) => {
    setEffectiveConditionalFormatting(newConditionalFormatting);
  }, []);

  const handleGroupingChange = useCallback((newGrouping) => {
    setEffectiveGrouping(newGrouping);
  }, []);

  const handleSummaryPreferencesChange = useCallback((newSummaryPreferences) => {
    setEffectiveSummaryPreferences(newSummaryPreferences);
  }, []);

  // Mutation to save summary preferences to the view
  const { mutate: saveSummaryPreferences } = useMutation(
    async (summaryPreferences) => {
      try {
        await viewsApi.patch({
          endpoint: "update",
          body: {
            column: "summary_preferences",
            value: summaryPreferences,
            id: baseMetadata?.view_id
          }
        });

        // Invalidate the query for the smart table data
        queryClient.invalidateQueries(["smart-table-complete-data", { tableId }]);

      } catch (error) {
        console.error("Error saving summary preferences:", error);
      }
    },
    {
      onSuccess: (data, variables) => {
        console.log('Summary preferences saved successfully');
      },
      onError: (error) => {
        console.error("Error saving summary preferences:", error);
      }
    }
  );

  // Handler to update summary preference for a specific column
  const handleSummaryPreferenceChange = useCallback((colId, summaryType) => {
    const newSummaryPreferences = {
      ...effectiveSummaryPreferences,
      [colId]: summaryType
    };

    // Update local state immediately
    setEffectiveSummaryPreferences(newSummaryPreferences);

    // Save to server
    saveSummaryPreferences(newSummaryPreferences);
  }, [effectiveSummaryPreferences, saveSummaryPreferences]);

  const frozenGridRef = useRef(null);
  const frozenHeaderGridRef = useRef(null);
  const frozenFooterGridRef = useRef(null);
  const scrollableGridRef = useRef(null);
  const scrollableHeaderGridRef = useRef(null);
  const scrollableFooterGridRef = useRef(null);
  const scrollableHeaderContainerRef = useRef(null);
  const scrollableFooterContainerRef = useRef(null);
  const lastScrollLeftRef = useRef(0);
  const searchInputRef = useRef(null);

  // Auto-focus search input when activated
  useEffect(() => {
    if (isSearchActive && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isSearchActive]);

  // Prevent browser's default Ctrl+F when our search is active
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.ctrlKey && e.key === 'f') {
        e.preventDefault();
        if (isSearchActive && searchInputRef.current) {
          searchInputRef.current.focus();
        } else {
          setIsSearchActive(true);
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isSearchActive]);

  // Hide tooltip on scroll or click elsewhere
  useEffect(() => {
    const handleScroll = () => {
      if (tooltip.show) {
        hideTooltip();
      }
    };

    const handleClick = () => {
      if (tooltip.show) {
        hideTooltip();
      }
    };

    if (tooltip.show) {
      window.addEventListener('scroll', handleScroll, true);
      window.addEventListener('click', handleClick, true);

      return () => {
        window.removeEventListener('scroll', handleScroll, true);
        window.removeEventListener('click', handleClick, true);
      };
    }
  }, [tooltip.show]);

  const frozenDataColumns = useMemo(() => {
    if (!displayVisibleColumns || displayVisibleColumns.length === 0) return [];
    return [displayVisibleColumns[0]];
  }, [displayVisibleColumns]);

  const scrollableDataColumns = useMemo(() => {
    if (!displayVisibleColumns || displayVisibleColumns.length <= 1) return [];
    return displayVisibleColumns.slice(1);
  }, [displayVisibleColumns]);

  const onScroll = useCallback(({ scrollTop, scrollLeft, scrollUpdateWasRequested }) => {
    if (scrollUpdateWasRequested) return;

    if (frozenGridRef.current) {
      frozenGridRef.current.scrollTo({ scrollTop });
    }

    lastScrollLeftRef.current = scrollLeft;
    requestAnimationFrame(() => {
      if (scrollableHeaderContainerRef.current) {
        scrollableHeaderContainerRef.current.scrollLeft = lastScrollLeftRef.current;
      }
      if (scrollableFooterContainerRef.current) {
        scrollableFooterContainerRef.current.scrollLeft = lastScrollLeftRef.current;
      }
    });
  }, []);

  const accessToken = useSelector((state) => state.user.accessToken);


  // Helper functions for managing cell editing state
  const startEditingCell = useCallback((rowId, colId) => {
    setEditingCell({ rowId, colId });
  }, []);

  const stopEditingCell = useCallback(() => {
    setEditingCell(null);
  }, []);

  const isCellEditing = useCallback((rowId, colId) => {
    return editingCell && editingCell.rowId === rowId && editingCell.colId === colId;
  }, [editingCell]);

  const handleCellChange = useCallback((rowId, colId, newValue) => {
    if (socket) {
      // Get current value for potential rollback
      const currentValue = allRows?.find(r => r.row_id === rowId)?.[colId];

      console.log(`⚡ OPTIMISTIC UPDATE: ${colId} from "${currentValue}" to "${newValue}" for row ${rowId}`);

      // Close the editing state first
      stopEditingCell();

      // 1. Optimistically update the cache directly
      queryClient.setQueryData(["smart-table-complete-data", { tableId }], (oldData) => {
        if (!oldData) return oldData;

        return {
          ...oldData,
          rows: oldData.rows.map(row => {
            if (row.row_id === rowId) {
              return { ...row, [colId]: newValue };
            }
            return row;
          })
        };
      });

      // 2. Emit the change to the server
      socket.emit('cell_update', {
        tableId,
        viewId: selectedViewId,
        rowId,
        colId,
        newValue,
        originalValue: currentValue, // Send previous value for server-side comparison
        accessToken
      });
    }
  }, [socket, tableId, selectedViewId, accessToken, allRows, queryClient, stopEditingCell]); // Updated dependencies

  // Helper function to get column width
  const getColumnWidth = useCallback((colId) => {
    // First check if there's a custom width set
    if (effectiveColumnWidths[colId]) {
      return effectiveColumnWidths[colId];
    }

    // If no custom width, look for default_width in column definition
    const columnDef = baseMetadata?.columns?.find(col => col.col_id === colId);
    if (columnDef?.default_width) {
      return columnDef.default_width;
    }

    // Fall back to the global default
    return DEFAULT_COLUMN_WIDTH;
  }, [effectiveColumnWidths, baseMetadata?.columns]);

  // Helper function to get frozen column width
  const getFrozenColumnWidth = useCallback(() => {
    if (frozenDataColumns.length > 0) {
      return getColumnWidth(frozenDataColumns[0].col_id);
    }
    return FROZEN_COLUMN_WIDTH; // fallback
  }, [frozenDataColumns, getColumnWidth]);

  // Utility function to evaluate conditional formatting rules
  const evaluateConditionalFormatting = useCallback((rowItem, colId) => {
    if (!effectiveConditionalFormatting || effectiveConditionalFormatting.length === 0) {
      return null;
    }

    // Helper function to parse comparison value and return a Date object
    const parseComparisonDate = (comparisonValue) => {
      if (!comparisonValue) return null;

      // Handle the new format: "type:value" or just "type" for predefined options
      let comparisonType, comparisonData;
      if (typeof comparisonValue === 'string' && comparisonValue.includes(':')) {
        [comparisonType, comparisonData] = comparisonValue.split(':');
      } else if (typeof comparisonValue === 'string') {
        // Check if it looks like an ISO date string (exact date stored without prefix)
        if (comparisonValue.match(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/)) {
          const exactDate = new Date(comparisonValue);
          // Normalize to midnight UTC for consistent date-only comparison
          return new Date(Date.UTC(exactDate.getUTCFullYear(), exactDate.getUTCMonth(), exactDate.getUTCDate()));
        }
        comparisonType = comparisonValue;
        comparisonData = '';
      } else {
        comparisonType = comparisonValue;
        comparisonData = '';
      }

      const now = new Date();
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

      switch (comparisonType) {
        case 'today':
          return today;
        case 'tomorrow':
          return new Date(today.getTime() + 24 * 60 * 60 * 1000);
        case 'yesterday':
          return new Date(today.getTime() - 24 * 60 * 60 * 1000);
        case 'one_week_ago':
          return new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000);
        case 'one_week_from_now':
          return new Date(today.getTime() + 7 * 24 * 60 * 60 * 1000);
        case 'one_month_ago':
          const oneMonthAgo = new Date(today);
          oneMonthAgo.setMonth(oneMonthAgo.getMonth() - 1);
          return oneMonthAgo;
        case 'one_month_from_now':
          const oneMonthFromNow = new Date(today);
          oneMonthFromNow.setMonth(oneMonthFromNow.getMonth() + 1);
          return oneMonthFromNow;
        case 'number_of_days_ago':
          const daysAgo = parseInt(comparisonData, 10);
          if (isNaN(daysAgo)) return null;
          return new Date(today.getTime() - daysAgo * 24 * 60 * 60 * 1000);
        case 'number_of_days_from_now':
          const daysFromNow = parseInt(comparisonData, 10);
          if (isNaN(daysFromNow)) return null;
          return new Date(today.getTime() + daysFromNow * 24 * 60 * 60 * 1000);
        case 'exact_date':
          const exactDate = comparisonData ? new Date(comparisonData) : new Date(comparisonValue);
          // Normalize to midnight UTC for consistent date-only comparison
          return new Date(Date.UTC(exactDate.getUTCFullYear(), exactDate.getUTCMonth(), exactDate.getUTCDate()));
        default:
          // Legacy support for direct date strings
          return new Date(comparisonValue);
      }
    };

    // Find the first matching rule for this column
    for (const rule of effectiveConditionalFormatting) {
      if (rule.col_id !== colId) continue;

      let cellValue = rowItem[colId];
      if (colId === "advisor") {
        cellValue = rowItem[colId].user_id;
      }
      console.log('cellValue', cellValue);
      console.log('rule', rule);
      let matches = false;

      // Evaluate the condition based on operator and column type
      switch (rule.operator) {
        case "is not empty":
          matches = cellValue !== null && cellValue !== undefined && cellValue !== "";
          break;
        case "is empty":
          matches = cellValue === null || cellValue === undefined || cellValue === "";
          break;
        case "contains":
          matches = cellValue && String(cellValue).toLowerCase().includes(String(rule.comparison || "").toLowerCase());
          break;
        case "does not contain":
          matches = !cellValue || !String(cellValue).toLowerCase().includes(String(rule.comparison || "").toLowerCase());
          break;
        case "is exactly":
          matches = String(cellValue) === String(rule.comparison);
          break;
        case "is not exactly":
          matches = String(cellValue) !== String(rule.comparison);
          break;
        case ">":
          matches = parseFloat(cellValue) > parseFloat(rule.comparison);
          break;
        case ">=":
          matches = parseFloat(cellValue) >= parseFloat(rule.comparison);
          break;
        case "<":
          matches = parseFloat(cellValue) < parseFloat(rule.comparison);
          break;
        case "<=":
          matches = parseFloat(cellValue) <= parseFloat(rule.comparison);
          break;
        case "=":
          matches = parseFloat(cellValue) === parseFloat(rule.comparison);
          break;
        case "!=":
          matches = parseFloat(cellValue) !== parseFloat(rule.comparison);
          break;
        case "is before":
        case "before": // Legacy support
          const beforeDate = parseComparisonDate(rule.comparison);
          if (beforeDate && cellValue) {
            const cellDate = new Date(cellValue);
            const cellDateNormalized = new Date(Date.UTC(cellDate.getUTCFullYear(), cellDate.getUTCMonth(), cellDate.getUTCDate()));
            const beforeDateNormalized = new Date(Date.UTC(beforeDate.getUTCFullYear(), beforeDate.getUTCMonth(), beforeDate.getUTCDate()));
            matches = cellDateNormalized < beforeDateNormalized;
          }
          break;
        case "is after":
        case "after": // Legacy support
          const afterDate = parseComparisonDate(rule.comparison);
          if (afterDate && cellValue) {
            const cellDate = new Date(cellValue);
            const cellDateNormalized = new Date(Date.UTC(cellDate.getUTCFullYear(), cellDate.getUTCMonth(), cellDate.getUTCDate()));
            const afterDateNormalized = new Date(Date.UTC(afterDate.getUTCFullYear(), afterDate.getUTCMonth(), afterDate.getUTCDate()));
            matches = cellDateNormalized > afterDateNormalized;
          }
          break;
        case "is":
          if (rule.col_type === "date") {
            // Handle date comparison for "is" operator
            const isDate = parseComparisonDate(rule.comparison);
            if (isDate && cellValue) {
              const cellDate = new Date(cellValue);
              const cellDateNormalized = new Date(Date.UTC(cellDate.getUTCFullYear(), cellDate.getUTCMonth(), cellDate.getUTCDate()));
              const isDateNormalized = new Date(Date.UTC(isDate.getUTCFullYear(), isDate.getUTCMonth(), isDate.getUTCDate()));
              matches = cellDateNormalized.getTime() === isDateNormalized.getTime();
            }
          } else {
            matches = cellValue === rule.comparison;
          }
          break;
        case "is not":
          if (rule.col_type === "date") {
            // Handle date comparison for "is not" operator
            const isNotDate = parseComparisonDate(rule.comparison);
            if (isNotDate && cellValue) {
              const cellDate = new Date(cellValue);
              const cellDateNormalized = new Date(Date.UTC(cellDate.getUTCFullYear(), cellDate.getUTCMonth(), cellDate.getUTCDate()));
              const isNotDateNormalized = new Date(Date.UTC(isNotDate.getUTCFullYear(), isNotDate.getUTCMonth(), isNotDate.getUTCDate()));
              matches = cellDateNormalized.getTime() !== isNotDateNormalized.getTime();
            }
          } else {
            matches = cellValue !== rule.comparison;
          }
          break;
        case "is on or before":
          const onOrBeforeDate = parseComparisonDate(rule.comparison);
          if (onOrBeforeDate && cellValue) {
            const cellDate = new Date(cellValue);
            const cellDateNormalized = new Date(Date.UTC(cellDate.getUTCFullYear(), cellDate.getUTCMonth(), cellDate.getUTCDate()));
            const onOrBeforeDateNormalized = new Date(Date.UTC(onOrBeforeDate.getUTCFullYear(), onOrBeforeDate.getUTCMonth(), onOrBeforeDate.getUTCDate()));
            matches = cellDateNormalized <= onOrBeforeDateNormalized;
          }
          break;
        case "is on or after":
          const onOrAfterDate = parseComparisonDate(rule.comparison);
          if (onOrAfterDate && cellValue) {
            const cellDate = new Date(cellValue);
            const cellDateNormalized = new Date(Date.UTC(cellDate.getUTCFullYear(), cellDate.getUTCMonth(), cellDate.getUTCDate()));
            const onOrAfterDateNormalized = new Date(Date.UTC(onOrAfterDate.getUTCFullYear(), onOrAfterDate.getUTCMonth(), onOrAfterDate.getUTCDate()));
            matches = cellDateNormalized >= onOrAfterDateNormalized;
          }
          break;
        case "includes":
          matches = Array.isArray(cellValue) ? cellValue.includes(rule.comparison) : cellValue === rule.comparison;
          break;
        case "does not include":
          matches = Array.isArray(cellValue) ? !cellValue.includes(rule.comparison) : cellValue !== rule.comparison;
          break;
        case "checked":
          matches = !!cellValue;
          break;
        case "not checked":
          matches = !cellValue;
          break;
        default:
          matches = false;
      }

      if (matches) {
        return {
          backgroundColor: rule.background_color,
          color: rule.text_color,
        };
      }
    }

    return null;
  }, [effectiveConditionalFormatting]);

  if (isError && !allRows) return <div>There was an error fetching table data. Please try again.</div>;

  // Smart Table Skeleton Loading Component
  const SmartTableSkeleton = () => (
    <div className="animate-pulse">
      {/* Header Skeleton */}
      <div className="flex border-b border-gray-200 bg-gray-100">
        <div className="flex-shrink-0" style={{ width: FROZEN_COLUMN_WIDTH }}>
          <div className="h-[50px] bg-gray-200 rounded m-2"></div>
        </div>
        <div className="flex space-x-4 p-2 flex-1">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="h-[34px] bg-gray-200 rounded" style={{ width: DEFAULT_COLUMN_WIDTH }}></div>
          ))}
        </div>
      </div>

      {/* Body Rows Skeleton */}
      {[...Array(8)].map((_, rowIndex) => (
        <div key={rowIndex} className="flex border-b border-gray-100">
          <div className="flex-shrink-0" style={{ width: FROZEN_COLUMN_WIDTH }}>
            <div className="h-[50px] bg-gray-100 rounded m-2"></div>
          </div>
          <div className="flex space-x-4 p-2 flex-1">
            {[...Array(4)].map((_, colIndex) => (
              <div
                key={colIndex}
                className={`h-[34px] rounded ${rowIndex % 2 === 0 ? 'bg-gray-100' : 'bg-gray-50'}`}
                style={{ width: DEFAULT_COLUMN_WIDTH }}
              ></div>
            ))}
          </div>
        </div>
      ))}

      {/* Footer Summary Skeleton */}
      <div className="flex border-t border-gray-200 bg-gray-50">
        <div className="flex-shrink-0" style={{ width: FROZEN_COLUMN_WIDTH }}>
          <div className="h-[50px] bg-gray-200 rounded m-2"></div>
        </div>
        <div className="flex space-x-4 p-2 flex-1">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="h-[34px] bg-gray-200 rounded" style={{ width: DEFAULT_COLUMN_WIDTH }}></div>
          ))}
        </div>
      </div>
    </div>
  );

  // Improved Error State Component
  const ErrorState = () => (
    <div className="flex items-center justify-center h-64">
      <div className="bg-red-50 border border-red-200 rounded-lg p-6 max-w-md">
        <div className="flex items-start gap-3">
          <div className="p-2 bg-red-100 rounded-lg">
            <InformationCircleIcon className="h-6 w-6 text-red-600" />
          </div>
          <div>
            <h3 className="text-sm font-medium text-red-800">Unable to load table data</h3>
            <p className="text-sm text-red-700 mt-1">
              There was an error fetching the table data. Please check your connection and try refreshing the page.
            </p>
            <button
              onClick={() => window.location.reload()}
              className="mt-3 text-sm bg-red-600 text-white px-3 py-1 rounded hover:bg-red-700 transition-colors"
            >
              Refresh Page
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  const Cell = ({ columnIndex, rowIndex, style, data }) => {
    const { type, columns, rowsData, allColumnsMetadata, summariesData } = data;
    const rowItem = rowsData && rowsData[rowIndex];
    let col;

    if (type.startsWith('frozen-')) {
      if (columns.length > 0 && columnIndex === 0) {
        col = columns[0];
      }
    } else if (type.startsWith('scrollable-')) {
      if (columnIndex < columns.length) {
        col = columns[columnIndex];
      }
    }

    // Handle group header rows
    if (rowItem && rowItem.__isGroupHeader) {
      let cellClassName = "border-r border-gray-200 flex items-center";
      let specificStyle = { ...style };
      const groupLevel = rowItem.__groupLevel || 0;

      if (type === 'frozen-header' || type === 'scrollable-header' || type === 'frozen-footer' || type === 'scrollable-footer') {
        // For header and footer, use normal rendering
        cellClassName = classNames("p-2 border-r border-b border-gray-200 flex items-center", "bg-gray-100 font-semibold text-sm");
        if (col) {
          let content = (
            <div className="flex items-center text-sm">
              {col.title}
              {col.options?.editable && <PencilAltIcon className="h-4 w-4 ml-1 text-gray-500 flex-shrink-0" />}
              {col.description && <InformationCircleIcon className="h-4 w-4 ml-1 text-gray-500 flex-shrink-0" title={col.description} />}
            </div>
          );
          return (
            <div className={cellClassName} style={specificStyle} title={typeof content === 'string' ? content : undefined}>
              {content}
            </div>
          );
        }
      } else {
        // For group header in body, show group information only in the grouping column or first column
        const isGroupColumn = col && col.col_id === rowItem.__groupColId;
        const isFirstColumn = type === 'frozen-body' && columnIndex === 0;

        if (isGroupColumn || (isFirstColumn && !columns.find(c => c.col_id === rowItem.__groupColId))) {
          // Apply group header styling with better visual separation
          const indent = groupLevel * 16; // 16px per level

          cellClassName = classNames(
            "border-r flex items-center font-bold relative",
            // Top border for all group headers to create separation
            "border-t-2",
            // Level-specific styling
            groupLevel === 0 ? "bg-purple-100 border-purple-300 text-purple-900 py-3" :
              groupLevel === 1 ? "bg-indigo-50 border-indigo-200 text-indigo-800 py-2" :
                "bg-gray-100 border-gray-300 text-gray-800 py-2"
          );

          // Add bottom border for level 0 (main groups)
          if (groupLevel === 0) {
            cellClassName += " border-b border-purple-200";
          }

          specificStyle.paddingLeft = `${Math.max(12, parseInt(specificStyle.paddingLeft || 12) + indent)}px`;
          specificStyle.paddingRight = '12px';

          const content = (
            <div className="flex items-center w-full">
              <div className="flex items-center flex-1">
                {/* Level indicator with different styles */}
                <div className="flex items-center mr-3">
                  {groupLevel === 0 && (
                    <div className="w-3 h-3 rounded-sm bg-purple-500 mr-2 shadow-sm" />
                  )}
                  {groupLevel === 1 && (
                    <div className="w-2 h-2 rounded-full bg-indigo-400 mr-2" />
                  )}
                  {groupLevel >= 2 && (
                    <div className="w-1.5 h-1.5 rounded-full bg-gray-400 mr-2" />
                  )}
                  <span className={classNames(
                    "font-bold",
                    groupLevel === 0 ? "text-base" : "text-sm"
                  )}>
                    {rowItem.__groupKey}
                    {rowItem.__summariesOnly && (
                      <span className="ml-2 text-xs font-normal px-2 py-1 bg-amber-200 text-amber-800 rounded-full">
                        Summary
                      </span>
                    )}
                  </span>
                </div>
                <div className={classNames(
                  "px-2 py-1 rounded-full text-xs font-medium ml-auto",
                  groupLevel === 0 ? "bg-purple-200 text-purple-800" :
                    groupLevel === 1 ? "bg-indigo-200 text-indigo-700" :
                      "bg-gray-200 text-gray-700"
                )}>
                  {rowItem.__groupRowCount} {rowItem.__groupRowCount === 1 ? 'item' : 'items'}
                </div>
              </div>
            </div>
          );

          return (
            <div className={cellClassName} style={specificStyle}>
              {content}
            </div>
          );
        } else {
          // For other columns in group header row, show summary if available and enabled
          cellClassName = classNames(
            "border-r relative",
            // Top border for separation
            "border-t-2",
            groupLevel === 0 ? "bg-purple-100 border-purple-300 py-3" :
              groupLevel === 1 ? "bg-indigo-50 border-indigo-200 py-2" :
                "bg-gray-100 border-gray-300 py-2"
          );

          // Add bottom border for level 0 (main groups)
          if (groupLevel === 0) {
            cellClassName += " border-b border-purple-200";
          }

          let summaryContent = null;

          // Show summary for this column if group summaries are enabled and available
          if (rowItem.__showSummary && rowItem.__groupSummaries && col && rowItem.__groupSummaries[col.col_id]) {
            summaryContent = (
              <div className="flex items-center justify-end h-full px-2">
                <SummaryColumnDisplay
                  data={rowItem.__groupSummaries[col.col_id]}
                  col={col}
                  summaryPreferences={effectiveSummaryPreferences}
                  onSummaryPreferenceChange={handleSummaryPreferenceChange}
                />
              </div>
            );
          }

          return (
            <div className={cellClassName} style={specificStyle}>
              {summaryContent}
            </div>
          );
        }
      }
    }

    let content = "";
    let cellClassName = "p-2 border-r border-b border-gray-200 flex items-center";
    let specificStyle = { ...style };
    if ((col?.col_type === "text" || col?.col_type === "date" || col?.col_type === "number") && type === "scrollable-body") {
      cellClassName = "border-r border-b border-gray-200 flex items-center"
    }
    if (type === 'frozen-header') {
      cellClassName = classNames("p-2 border-r border-b border-gray-200 flex items-center", "bg-gray-100 font-semibold text-sm");
      if (col) {
        content = (
          <div className="flex items-center min-w-0 w-full">
            {/* Row count header for first column */}
            {columnIndex === 0 && (
              <span className="text-xs text-gray-500 mr-2 flex-shrink-0">#</span>
            )}
            <span
              className="truncate mr-1 flex-1 min-w-0 cursor-default text-sm"
              onMouseEnter={(e) => showTooltip(e, col.title, col.description)}
              onMouseLeave={hideTooltip}
            >
              {col.title}
            </span>
            {col.options?.editable && <PencilAltIcon className="h-4 w-4 ml-1 text-gray-500 flex-shrink-0" />}
            {col.description && <InformationCircleIcon className="h-4 w-4 ml-1 text-gray-500 flex-shrink-0" title={col.description} />}
          </div>
        );
        specificStyle.paddingLeft = '1.25rem';
      }
    } else if (type === 'scrollable-header') {
      cellClassName = classNames("p-2 border-r border-b border-gray-200 flex items-center", "bg-gray-100 font-semibold text-sm");
      if (col) {
        content = (
          <div className="flex items-center min-w-0 w-full">
            <span
              className="truncate mr-1 flex-1 min-w-0 cursor-default text-sm"
              onMouseEnter={(e) => showTooltip(e, col.title, col.description)}
              onMouseLeave={hideTooltip}
            >
              {col.title}
            </span>
            {col.options?.editable && <PencilAltIcon className="h-4 w-4 ml-1 text-gray-500 flex-shrink-0" />}
            {col.description && <InformationCircleIcon className="h-4 w-4 ml-1 text-gray-500 flex-shrink-0" title={col.description} />}
          </div>
        );
      }
    }
    else if (type === 'frozen-footer') {
      cellClassName = classNames(cellClassName, "bg-gray-50");
      if (col && summariesData) {
        content = <SummaryColumnDisplay
          data={summariesData[col.col_id]}
          col={col}
          summaryPreferences={effectiveSummaryPreferences}
          onSummaryPreferenceChange={handleSummaryPreferenceChange}
        />;
        specificStyle.paddingLeft = '1.25rem';
      }
    } else if (type === 'scrollable-footer') {
      cellClassName = classNames(cellClassName, "bg-gray-50");
      if (col && summariesData) {
        content = <SummaryColumnDisplay
          data={summariesData[col.col_id]}
          col={col}
          summaryPreferences={effectiveSummaryPreferences}
          onSummaryPreferenceChange={handleSummaryPreferenceChange}
        />;
      }
    }
    else if (rowItem && col) {
      cellClassName = classNames(cellClassName, "bg-white");
      if (type === 'frozen-body' || type === 'scrollable-body') {
        specificStyle.overflow = 'visible';
      }
      if (type === 'frozen-body' && columnIndex === 0) {
        specificStyle.paddingLeft = '1.25rem';
      }

      // Apply conditional formatting if rules match
      const conditionalStyle = evaluateConditionalFormatting(rowItem, col.col_id);
      if (conditionalStyle) {
        specificStyle.backgroundColor = conditionalStyle.backgroundColor;
        specificStyle.color = conditionalStyle.color;
      }

      const commonProps = {
        rowId: rowItem.row_id,
        initText: rowItem[col.col_id],
        initValue: rowItem[col.col_id],
        accessToken: accessToken,
        table: col.table,
        colId: col.col_id,
        editable: col.options?.editable,
        colTitle: col.title,
        thisRowIndex: rowIndex,
        totalRows: rowsData.length,
        editId: col.edit_id ? rowItem[col.edit_id] : rowItem.row_id,
      };

      let cellContent;
      switch (col.col_type) {
        case "text": cellContent = <Text {...commonProps} onCellChange={handleCellChange} />; break;
        case "claim_household": cellContent = <ClaimHousehold {...commonProps} data={rowItem[col.col_id]} onCellChange={handleCellChange} />; break;
        case "checkbox": cellContent = <Checkbox {...commonProps} onCellChange={handleCellChange} />; break;
        case "button": cellContent = <ButtonComponent rowData={rowItem} colId={col.col_id} buttonFunctions={buttonFunctions} />; break;
        case "number": cellContent = <SmartTableNumber {...commonProps} format={col.options?.format} onCellChange={handleCellChange} />; break;
        case "household_link": cellContent = <HouseholdLink data={{ household_name: rowItem.household_name, household_id: rowItem.household_id }} />; break;
        case "link":
          const linkData = rowItem[col.col_id];
          cellContent = <LinkColumn href={linkData?.href} text={linkData?.text} />;
          break;
        case "single_select": cellContent = <SingleSelect {...commonProps} selectOptions={baseMetadata?.columns?.find(c => c.col_id === col.col_id)?.select_options} onCellChange={handleCellChange} isEditing={isCellEditing(rowItem.row_id, col.col_id)} startEditing={() => startEditingCell(rowItem.row_id, col.col_id)} stopEditing={stopEditingCell} />; break;
        case "multi_select": cellContent = <MultiSelect {...commonProps} selectOptions={baseMetadata?.columns?.find(c => c.col_id === col.col_id)?.select_options} onCellChange={handleCellChange} isEditing={isCellEditing(rowItem.row_id, col.col_id)} startEditing={() => startEditingCell(rowItem.row_id, col.col_id)} stopEditing={stopEditingCell} />; break;
        case "copy_to_clipboard": cellContent = <CopyColumn textToCopy={rowItem[col.col_id]} />; break;
        case "date": cellContent = <DateComponent {...commonProps} initDate={rowItem[col.col_id]} showTime={col.options?.show_time} onCellChange={handleCellChange} />; break;
        case "advisor": cellContent = <AdvisorColumn advisor={rowItem[col.col_id]} />; break;
        default: cellContent = String(rowItem[col.col_id] !== undefined ? rowItem[col.col_id] : "");
      }

      // Add row number for first frozen column
      if (type === 'frozen-body' && columnIndex === 0 && !rowItem.__isGroupHeader) {
        content = (
          <div className="flex items-center w-full">
            <span className="text-xs text-gray-400 mr-3 flex-shrink-0 font-mono">
              {rowIndex + 1}
            </span>
            <div className="flex-1 min-w-0">
              {cellContent}
            </div>
          </div>
        );
      } else {
        content = cellContent;
      }
    }

    return (
      <div className={cellClassName} style={specificStyle} title={typeof content === 'string' ? content : undefined}>
        {content}
      </div>
    );
  };

  const gridItemData = (type, cols) => ({
    type,
    columns: cols,
    rowsData: type.includes('footer') ? [] : displayRows, // Don't pass row data for footer cells
    allColumnsMetadata: baseMetadata?.columns,
    summariesData: displaySummaries,
  });

  return (
    <Layout isSmartTable={true}>
      <div className="flex h-full">
        <div
          className={classNames(
            isShowingViews ? "w-[320px]" : "w-[0px] opacity-0",
            "flex-shrink-0 transition-all duration-300 ease-in-out border-r border-gray-300 overflow-hidden"
          )}
        >
          {isShowingViews && (
            <div className="px-3 h-full pt-4 flex flex-col">
              <button className="pl-2  flex-shrink-0" onClick={() => setIsShowingViews(false)}>
                <ChevronLeftIcon className="h-5 w-5" />
              </button>
              <div className="overflow-y-auto flex-1 min-h-0">
                <ViewsList
                  selectedViewId={selectedViewId}
                  setSelectedViewId={setSelectedViewId}
                  allViews={baseMetadata?.all_views}
                  smartTableId={tableId}
                />
              </div>
            </div>
          )}
        </div>

        <div className="flex-grow flex flex-col overflow-hidden">
          <div className="flex items-center justify-between h-[60px] px-5 border-b border-gray-300 flex-shrink-0">
            <div className="flex items-center space-x-2">
              {!isShowingViews && (
                <button className="p-1" onClick={() => setIsShowingViews(true)}>
                  <ChevronLeftIcon className="h-5 w-5 rotate-180" />
                </button>
              )}
              <div className={classNames(isFetching || isLoading ? "visible" : "invisible", "mr-3")}>
                <AiOutlineLoading3Quarters className="h-5 w-5 animate-spin" />
              </div>
              <FadeIn isShowing={(!isLoading && metadataForControls) ? true : false}>
                <div className="space-x-2 items-center hidden md:flex">
                  <SmartTableHiddenFields
                    dbHiddenColumns={effectiveHiddenColumns}
                    dbColumnOrder={effectiveColumnOrder}
                    dbColumnWidths={effectiveColumnWidths}
                    columns={baseMetadata?.columns}
                    viewId={baseMetadata?.view_id}
                    tableId={tableId}
                    onHiddenColumnsChange={handleHiddenColumnsChange}
                    onColumnOrderChange={handleColumnOrderChange}
                    onColumnWidthsChange={handleColumnWidthsChange}
                  />
                  <div className="">
                    {" "}
                    <SmartTableFilters
                      columns={baseMetadata?.columns}
                      dbFilters={effectiveFilters}
                      viewId={baseMetadata?.view_id}
                      tableId={tableId}
                      onFiltersChange={handleFiltersChange}
                    />
                  </div>
                  <SmartTableSorts
                    columns={baseMetadata?.columns}
                    dbSorts={effectiveSorts}
                    viewId={baseMetadata?.view_id}
                    tableId={tableId}
                    onSortsChange={handleSortsChange}
                  />

                  <SmartTableGrouping
                    columns={baseMetadata?.columns}
                    dbGrouping={effectiveGrouping}
                    viewId={baseMetadata?.view_id}
                    tableId={tableId}
                    onGroupingChange={handleGroupingChange}
                  />

                  <SmartTableConditionalFormatting
                    columns={baseMetadata?.columns}
                    dbConditionalFormatting={effectiveConditionalFormatting}
                    viewId={baseMetadata?.view_id}
                    tableId={tableId}
                    onConditionalFormattingChange={handleConditionalFormattingChange}
                  />

                  {/* Quick Search */}
                  <div className="flex items-center space-x-2">
                    {isSearchActive ? (
                      <div className="relative">
                        <input
                          ref={searchInputRef}
                          type="text"
                          placeholder="Search within view..."
                          value={searchQuery}
                          onChange={(e) => setSearchQuery(e.target.value)}
                          onBlur={() => {
                            if (!searchQuery.trim()) {
                              setIsSearchActive(false);
                            }
                          }}
                          onKeyDown={(e) => {
                            if (e.key === 'Escape') {
                              setSearchQuery("");
                              setIsSearchActive(false);
                            }
                          }}
                          className="w-64 px-3 py-1 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          autoFocus
                        />
                        {searchQuery && (
                          <button
                            onClick={() => {
                              setSearchQuery("");
                              setIsSearchActive(false);
                            }}
                            className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                          >
                            <XIcon className="h-4 w-4" />
                          </button>
                        )}
                      </div>
                    ) : (
                      <button
                        onClick={() => setIsSearchActive(true)}
                        className={classNames(
                          "rounded hover:ring-2 hover:ring-gray-300 text-gray-600 px-2 py-1 flex space-x-1 items-center",
                          searchQuery && "bg-blue-100 ring-2 ring-blue-400 ring-offset-1"
                        )}
                        title="Quick search (Ctrl+F)"
                      >
                        <SearchIcon className="h-5 w-5" />
                        <span className="hidden xl:flex text-sm">
                          Search{searchQuery && ` (${displayFilteredRowsLength} found)`}
                        </span>
                      </button>
                    )}
                  </div>

                  <p className="text-gray-500">
                    <span className="">{displayFilteredRowsLength}</span> / {metadataForControls?.total_rows_length || 0} (
                    {metadataForControls?.total_rows_length ? Math.floor(
                      (displayFilteredRowsLength / metadataForControls.total_rows_length) * 100
                    ) : 0}
                    %)
                  </p>
                </div>
              </FadeIn>
            </div>

            {/* Custom Control Buttons - Now properly justified to the end */}
            {customControlButtons && customControlButtons.length > 0 && (
              <ul className="hidden space-x-2 xl:flex items-center">
                {customControlButtons.map((button, index) => <li key={index}>{button}</li>)}
              </ul>
            )}
          </div>

          <div className="flex-grow overflow-auto custom-scrollbar-thick custom-scrollbar-gray" style={{ position: 'relative' }}>
            {isLoading && (
              <div className="h-full">
                <SmartTableSkeleton />
              </div>
            )}
            {isError && !allRows && (
              <ErrorState />
            )}
            {!isLoading && (allRows?.length > 0 || !isError) && metadataForControls && displayRows && (
              <AutoSizer>
                {({ height, width }) => {
                  const scrollbarPlaceholderWidth = 15;
                  const currentTotalFrozenWidth = frozenDataColumns.length > 0 ? getFrozenColumnWidth() : 0;
                  const scrollableGridWidth = Math.max(0, width - currentTotalFrozenWidth - scrollbarPlaceholderWidth);
                  const headerFooterHeight = ROW_HEIGHT;
                  const footerHeight = displaySummaries && Object.keys(displaySummaries).length > 0 ? ROW_HEIGHT : 0;
                  const bodyHeight = Math.max(0, height - headerFooterHeight - footerHeight);
                  const testFrozenGridWidth = currentTotalFrozenWidth;

                  return (
                    <div style={{ height, width, position: 'relative' }}>
                      {frozenDataColumns.length > 0 && (
                        <div style={{ position: 'sticky', top: 0, zIndex: 3, display: 'flex', width: width }}>
                          <Grid
                            ref={frozenHeaderGridRef}
                            columnCount={frozenDataColumns.length}
                            columnWidth={() => getFrozenColumnWidth()}
                            height={headerFooterHeight}
                            rowCount={1}
                            rowHeight={() => headerFooterHeight}
                            width={testFrozenGridWidth}
                            itemData={gridItemData('frozen-header', frozenDataColumns)}
                            style={{ overflow: 'hidden' }} >{Cell}</Grid>
                          <div ref={scrollableHeaderContainerRef} style={{ width: scrollableGridWidth, overflow: 'hidden' }} className="scrollable-header-container">
                            <Grid
                              ref={scrollableHeaderGridRef}
                              columnWidth={(index) => getColumnWidth(scrollableDataColumns[index]?.col_id)}
                              width={scrollableDataColumns.reduce((total, col) => total + getColumnWidth(col.col_id), 0)}
                              className="header-grid scrollable-header-grid"
                              style={{ overflow: 'hidden' }}
                              columnCount={scrollableDataColumns.length}
                              height={headerFooterHeight}
                              rowCount={1}
                              rowHeight={() => headerFooterHeight}
                              itemData={gridItemData('scrollable-header', scrollableDataColumns)}>{Cell}</Grid>
                          </div>
                          <div style={{ width: scrollbarPlaceholderWidth, flexShrink: 0 }} className="bg-gray-100" />
                        </div>
                      )}

                      <div style={{ display: 'flex', height: bodyHeight, width: width }}>
                        {frozenDataColumns.length > 0 && (
                          <Grid
                            ref={frozenGridRef}
                            columnCount={frozenDataColumns.length}
                            columnWidth={() => getFrozenColumnWidth()}
                            height={bodyHeight}
                            rowCount={displayRows.length}
                            rowHeight={() => ROW_HEIGHT}
                            width={testFrozenGridWidth}
                            itemData={gridItemData('frozen-body', frozenDataColumns)}
                            style={{ overflowX: 'hidden', overflowY: 'hidden' }} className="body-grid frozen-body-grid">{Cell}</Grid>
                        )}
                        <Grid
                          ref={scrollableGridRef}
                          onScroll={onScroll}
                          columnCount={scrollableDataColumns.length}
                          columnWidth={(index) => getColumnWidth(scrollableDataColumns[index]?.col_id)}
                          height={bodyHeight}
                          rowCount={displayRows.length}
                          rowHeight={() => ROW_HEIGHT}
                          width={scrollableGridWidth}
                          itemData={gridItemData('scrollable-body', scrollableDataColumns)}
                        >{Cell}</Grid>
                      </div>

                      {displaySummaries && Object.keys(displaySummaries).length > 0 && (
                        <div style={{ position: 'sticky', bottom: 0, zIndex: 3, display: 'flex', width: width }}>
                          {frozenDataColumns.length > 0 && (
                            <Grid
                              ref={frozenFooterGridRef}
                              columnCount={frozenDataColumns.length}
                              columnWidth={() => getFrozenColumnWidth()}
                              height={headerFooterHeight}
                              rowCount={1}
                              rowHeight={() => headerFooterHeight}
                              width={testFrozenGridWidth}
                              itemData={gridItemData('frozen-footer', frozenDataColumns)}
                              style={{ overflow: 'hidden' }} className="footer-grid frozen-footer-grid">{Cell}</Grid>
                          )}
                          <div ref={scrollableFooterContainerRef} style={{ width: scrollableGridWidth, overflow: 'hidden' }} className="scrollable-footer-container">
                            <Grid
                              ref={scrollableFooterGridRef}
                              columnWidth={(index) => getColumnWidth(scrollableDataColumns[index]?.col_id)}
                              width={scrollableDataColumns.reduce((total, col) => total + getColumnWidth(col.col_id), 0)}
                              className="footer-grid scrollable-footer-grid"
                              style={{ overflow: 'hidden' }}
                              columnCount={scrollableDataColumns.length}
                              height={headerFooterHeight}
                              rowCount={1}
                              rowHeight={() => headerFooterHeight}
                              itemData={gridItemData('scrollable-footer', scrollableDataColumns)}>{Cell}</Grid>
                          </div>
                          <div style={{ width: scrollbarPlaceholderWidth, flexShrink: 0 }} className="bg-gray-50" />
                        </div>
                      )}
                    </div>
                  );
                }}
              </AutoSizer>
            )}
            {!isLoading && !isError && metadataForControls && displayRows && displayRows.length === 0 && allRows?.length > 0 && (
              <div className="p-5 text-center">No data matches your current filters.</div>
            )}
            {!isLoading && !isError && allRows && allRows.length === 0 && (
              <div className="p-5 text-center">There is no data for this table yet.</div>
            )}
          </div>
        </div>
      </div>

      {/* Custom Tooltip */}
      {tooltip.show && (
        <div
          className="fixed pointer-events-none z-50 max-w-xs"
          style={{
            left: `${tooltip.x}px`,
            top: `${tooltip.y}px`,
            transform: 'translateX(-50%)',
          }}
        >
          <div className="bg-gray-50 text-gray-800 text-sm rounded-lg px-3 py-2 shadow-lg border border-gray-300">
            <div className="relative text-gray-800">
              {/* Arrow pointing up */}
              <div
                className="absolute bottom-full left-1/2 w-0 h-0 border-l-4 border-r-4 border-b-4 border-l-transparent border-r-transparent border-b-gray-500"
                style={{ transform: 'translateX(-50%)' }}
              />
              {tooltip.content}
            </div>
          </div>
        </div>
      )}

    </Layout>
  );
};

export default SmartTable;
